import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api from "@/lib/assessments";
import { readAll } from "@/lib/assessments";
const formatApiError = (e) => (e && e.message) || "Unknown error";
import { Save, ChevronLeft } from "lucide-react";
import { MSE_FIELDS } from "@/data/instruments";

export default function MSE() {
  const navigate = useNavigate();
  const [patient, setPatient] = useState("");
  const [notes, setNotes] = useState("");
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const complete = MSE_FIELDS.every((f) => values[f.id]);

  const save = async () => {
    if (!complete) {
      toast.error("Please make a selection for every MSE domain.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/assessments", {
        kind: "mse",
        patient_identifier: patient || null,
        responses: values,
        interpretation: values.thought_content?.includes("SI") || values.thought_content?.includes("AH/VH")
          ? "Findings warrant safety follow-up"
          : "MSE documented",
        notes: notes || null,
      });
      toast.success("MSE saved.");
      navigate("/portal/continuum/tools/history");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer testid="mse-page">
      <Link to="/portal/continuum/tools" className="text-sm text-ink-muted hover:text-navy inline-flex items-center gap-1 mb-4" data-testid="mse-back">
        <ChevronLeft size={14} strokeWidth={2} /> All tools
      </Link>

      <PageHeader overline="Portal · Tool" title="Mental Status Exam"
        subtitle="Ten-domain MSE documented on one screen. Select the closest descriptor per domain; add free-text notes as needed." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <label className="label">Patient identifier</label>
            <input className="field" value={patient} onChange={(e) => setPatient(e.target.value)} placeholder="Initials or MRN" data-testid="mse-patient" />
          </div>

          <div className="card p-5 space-y-4">
            {MSE_FIELDS.map((f) => (
              <div key={f.id} data-testid={`mse-field-${f.id}`}>
                <label className="label">{f.label}</label>
                <div className="flex flex-wrap gap-2">
                  {f.options.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setValues({ ...values, [f.id]: opt })}
                      className={`btn text-sm ${values[f.id] === opt ? "btn-primary" : "btn-secondary"}`}
                      data-testid={`mse-${f.id}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="card p-5">
            <label className="label">Additional narrative</label>
            <textarea className="field min-h-[100px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="mse-notes" />
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-6" data-testid="mse-summary">
            <div className="overline mb-2">Documented findings</div>
            {complete ? (
              <ul className="text-sm space-y-1 fade-up">
                {MSE_FIELDS.map((f) => (
                  <li key={f.id} className="flex items-baseline justify-between gap-2">
                    <span className="text-ink-muted text-xs">{f.label}</span>
                    <span className="text-navy font-medium text-sm text-right">{values[f.id]}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-ink-muted">
                {MSE_FIELDS.filter((f) => !values[f.id]).length} domain
                {MSE_FIELDS.filter((f) => !values[f.id]).length === 1 ? "" : "s"} remaining.
              </div>
            )}

            <div className="rule my-4" />
            <button onClick={save} disabled={!complete || saving} className="btn btn-primary w-full justify-center" data-testid="mse-save">
              {saving ? "Saving..." : (<><Save size={14} strokeWidth={2} /> Save to history</>)}
            </button>
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}
